import React from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Box,
  useTheme,
  useMediaQuery,
  Typography,
  Divider,
  Button,
} from '@mui/material';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import DashboardIcon from '@mui/icons-material/Dashboard';
import HistoryIcon from '@mui/icons-material/History';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import LogoutIcon from '@mui/icons-material/Logout';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';

const drawerWidth = 280;

const menuItems = [
  { text: 'Dashboard', path: '/dashboard', icon: <DashboardIcon />, isProtected: true, isHidden: false },
  { text: 'Finance', path: '/finance', icon: <AccountBalanceIcon />, isProtected: true, isHidden: false },
  { text: 'Pin Code History', path: '/pincode-history', icon: <LocalShippingIcon />, isProtected: true, isHidden: false },
  { text: 'Orders', path: '/orders', icon: <ShoppingBagIcon />, isProtected: true, isHidden: false },
  { text: 'Analytics', path: '/analytics-traffic', icon: <AnalyticsIcon />, isProtected: true, isHidden: false },
  { text: 'Expenses', path: '/expenses', icon: <ReceiptIcon />, isProtected: true, isHidden: false },
  { text: 'Products', path: '/products', icon: <AddShoppingCartIcon />, isProtected: true, isHidden: false },
];

const Sidebar = ({ open, onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const location = useLocation();
  const navigate = useNavigate();
  const isAuthenticated = !!localStorage.getItem('token');

  const isActive = (path) => {
    if (path === '/orders' && (location.pathname.startsWith('/order/') || location.pathname.startsWith('/orders/'))) {
      return true;
    }
    return location.pathname === path;
  };

  const handleLogout = () => {
    if (!window.confirm('Are you sure you want to log out?')) return;
    localStorage.removeItem('token');
    navigate('/');
    if (isMobile && onClose) {
      onClose();
    }
  };

  const filteredMenuItems = menuItems.filter((item) =>
    !isAuthenticated ? !item.isProtected : !item.isHidden
  );

  const drawer = (
    <Box sx={{
      overflow: 'auto',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      background: 'rgba(255, 255, 255, 0.7)',
    }}>
      <Box sx={{ p: 4, display: { xs: 'none', lg: 'flex' }, alignItems: 'center', gap: 2.5, mb: 1 }}>
        <Box
          component="img"
          src="/pink_logo.png"
          alt="Ishqnama art Logo"
          sx={{
            height: 48,
            filter: 'drop-shadow(0 4px 10px rgba(181, 29, 110, 0.15))',
          }}
        />
        <Box
          component="img"
          src="/namelogo.png"
          alt="Ishqnama art Logo"
          sx={{
            height: 36,
          }}
        />
      </Box>
      <Divider sx={{ opacity: 0.05, mx: 2 }} />
      <List sx={{ pt: { xs: 8, lg: 0 }, px: 2.5, flex: 1 }}>
        {filteredMenuItems.map((item) => (
          <ListItem key={item.text} disablePadding sx={{ mb: 1.25 }}>
            <ListItemButton
              component={RouterLink}
              to={item.path}
              onClick={isMobile ? onClose : undefined}
              selected={isActive(item.path)}
              sx={{
                borderRadius: '14px',
                py: 1.75,
                px: 2,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  backgroundColor: 'rgba(181, 29, 110, 0.05)',
                  transform: 'translateX(8px)',
                },
                '&.Mui-selected': {
                  background: 'linear-gradient(135deg, #B51D6E 0%, #E91E63 100%)',
                  boxShadow: '0 8px 20px rgba(181, 29, 110, 0.3)',
                  color: 'white',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #E91E63 0%, #B51D6E 100%)',
                  },
                  '& .MuiListItemIcon-root': {
                    color: 'white',
                  },
                  '& .MuiTypography-root': {
                    color: 'white',
                  }
                },
              }}
            >
              <ListItemIcon sx={{
                color: isActive(item.path) ? 'white' : 'text.secondary',
                minWidth: 40,
                transition: 'all 0.3s',
              }}>
                {React.cloneElement(item.icon, {
                  sx: { fontSize: 22 }
                })}
              </ListItemIcon>
              <ListItemText
                primary={item.text}
                primaryTypographyProps={{
                  fontSize: '0.9rem',
                  fontWeight: isActive(item.path) ? 800 : 500,
                  letterSpacing: '0.01em',
                  color: isActive(item.path) ? 'white' : 'text.primary',
                }}
              />
              {isActive(item.path) && (
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    bgcolor: 'white',
                    borderRadius: '50%',
                    boxShadow: '0 0 10px rgba(255, 255, 255, 0.8)',
                  }}
                />
              )}
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      {isAuthenticated && (
        <Box sx={{ p: 3, mt: 'auto' }}>
          <Button
            fullWidth
            onClick={handleLogout}
            startIcon={<LogoutIcon />}
            sx={{
              borderRadius: '14px',
              py: 1.5,
              bgcolor: 'transparent !important', 
              color: '#EF4444', 
              fontWeight: 800, 
              boxShadow: 'none',
              '&:hover': {
                bgcolor: 'rgba(239, 68, 68, 0.05) !important',
                transform: 'translateY(-2px)',
              },
            }}
          >
            Logout
          </Button>
        </Box>
      )}
    </Box>
  );

  return (
    <>
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', lg: 'block' },
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            backgroundColor: 'transparent',
            borderRight: 'none',
            position: 'fixed',
            height: '100vh',
            top: 0,
          },
        }}
        open
      >
        {drawer}
      </Drawer>

      <Drawer
        variant="temporary"
        open={open}
        onClose={onClose}
        sx={{
          display: { xs: 'block', lg: 'none' },
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            backgroundColor: '#FFFFFF',
            borderRight: '1px solid rgba(0, 0, 0, 0.06)',
          },
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
};

export default Sidebar;